import React from 'react';
import { Award, GraduationCap, Users } from 'lucide-react';
import { doctors } from '../data/mock';

const About = () => {
  const stats = [
    { icon: Users, value: '2,500+', label: 'Happy Patients' },
    { icon: Award, value: '15+', label: 'Years Experience' },
    { icon: GraduationCap, value: '100%', label: 'Certified Specialists' },
  ];

  return (
    <section id="about" className="py-24 gradient-section relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-teal-50 text-teal-700 font-semibold text-sm rounded-full mb-4">
            About Us
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
            Meet Our Expert
            <span className="text-gradient"> Orthodontists</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Our team of board-certified orthodontists combines years of experience 
            with the latest techniques to deliver exceptional results.
          </p>
        </div>

        {/* Doctors Grid */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-20">
          {doctors.map((doctor, index) => (
            <div
              key={doctor.id}
              className={`group bg-white rounded-3xl shadow-lg hover:shadow-2xl card-hover overflow-hidden animate-fade-in-up`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className="flex flex-col sm:flex-row">
                {/* Image */}
                <div className="sm:w-2/5 relative overflow-hidden">
                  <img
                    src={doctor.image}
                    alt={doctor.name}
                    className="w-full h-64 sm:h-full object-cover img-zoom"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>

                {/* Content */}
                <div className="sm:w-3/5 p-6 lg:p-8">
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-slate-800 group-hover:text-teal-600 transition-colors">
                      {doctor.name}
                    </h3>
                    <p className="text-teal-600 font-semibold">{doctor.title}</p>
                    <p className="text-sm text-slate-500 mt-1">{doctor.credentials}</p>
                  </div>
                  <p className="text-slate-600 leading-relaxed">{doctor.bio}</p>
                  
                  {/* Social links placeholder */}
                  <div className="mt-6 pt-4 border-t border-slate-100">
                    <a
                      href="#appointment"
                      className="text-teal-600 font-semibold hover:text-teal-700 transition-colors"
                    >
                      Book with {doctor.name.split(' ')[1]} →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-3 gap-6 lg:gap-12">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className="text-center animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-2xl mb-4">
                <stat.icon className="w-8 h-8 text-teal-600" />
              </div>
              <div className="text-3xl lg:text-4xl font-bold text-slate-800 mb-1">
                {stat.value}
              </div>
              <div className="text-slate-500 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Why Choose Us */}
        <div className="mt-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-slide-in-left">
            <h3 className="text-2xl lg:text-3xl font-bold text-slate-800 mb-6">
              Why Families Choose Us
            </h3>
            <div className="space-y-6">
              {[
                {
                  title: 'State-of-the-Art Technology',
                  desc: 'We use the latest 3D imaging, digital scanning, and treatment planning technology for precise, efficient care.',
                },
                {
                  title: 'Personalized Treatment Plans',
                  desc: 'Every smile is unique. We create customized treatment plans tailored to your specific needs and lifestyle.',
                },
                {
                  title: 'Comfortable Environment',
                  desc: 'Our modern, welcoming office is designed to make you feel at ease from the moment you walk in.',
                },
                {
                  title: 'Flexible Scheduling',
                  desc: 'We offer early morning, evening, and Saturday appointments to fit your busy schedule.',
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="shrink-0 w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                    <span className="text-teal-600 font-bold">{i + 1}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-1">{item.title}</h4>
                    <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative animate-slide-in-right">
            <img
              src="https://images.unsplash.com/photo-1629909615184-74f495363b67?w=600&h=500&fit=crop"
              alt="Modern orthodontic clinic"
              className="rounded-3xl shadow-xl w-full object-cover"
            />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-800">Top Rated</p>
                  <p className="text-sm text-slate-500">In California</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
